import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import 'ol/ol.css';
import queryString from 'query-string';
import { Alert, Box, Collapse, IconButton, Stack } from '@mui/material';
import { TileImage } from 'ol/source';
import TileLayer from 'ol/layer/Tile';
import { ImageTile } from 'ol';
import TileGrid from 'ol/tilegrid/TileGrid';
import TileState from 'ol/TileState';
import { Projection } from 'ol/proj';
import { Control, defaults as defaultControls } from 'ol/control';
import CloseIcon from '@mui/icons-material/Close';

import { useGetFileInfo, useGetFilePathAndInfo } from '../api/useGetImageTiffViewer';
import { useSessionStore } from 'session/useSessionStore';
import { fetchWithRetry } from 'utils/fetchWithRetry';

class RotateResetControl extends Control {
    private arrowElement: HTMLSpanElement;

    constructor(onReset: () => void) {
        const arrow = document.createElement('span');
        arrow.innerHTML = '&#10138;';
        arrow.style.cssText = [
            'display:inline-block',
            'color:#fff',
            'font-size:18px',
            'line-height:1',
            'transform:rotate(0deg)',
            'transition:transform 0.25s',
        ].join(';');

        const button = document.createElement('button');
        button.style.cssText = [
            'display:flex',
            'align-items:center',
            'justify-content:center',
            'width:36px',
            'height:36px',
            'background-color:#1976d2',
            'border:none',
            'border-radius:4px',
            'cursor:pointer',
            'box-shadow:0 2px 4px rgba(0,0,0,0.3)',
            'padding:0',
        ].join(';');
        button.appendChild(arrow);

        const tooltip = document.createElement('span');
        tooltip.textContent = 'Reset rotation';
        tooltip.style.cssText = [
            'position:absolute',
            'top:50%',
            'right:calc(100% + 8px)',
            'transform:translateY(-50%)',
            'background-color:rgba(0,0,0,0.75)',
            'color:#fff',
            'font-size:12px',
            'line-height:1',
            'padding:6px 8px',
            'border-radius:4px',
            'white-space:nowrap',
            'pointer-events:none',
            'opacity:0',
            'transition:opacity 0.15s',
        ].join(';');

        const element = document.createElement('div');
        element.style.cssText = 'position:absolute;top:8px;right:8px;';
        element.className = 'ol-unselectable ol-control';
        element.appendChild(button);
        element.appendChild(tooltip);

        button.addEventListener('mouseover', () => {
            tooltip.style.opacity = '1';
        });
        button.addEventListener('mouseout', () => {
            tooltip.style.opacity = '0';
        });

        super({ element });

        this.arrowElement = arrow;

        button.addEventListener('click', onReset);
    }

    setRotation(rotation: number): void {
        const degrees = (rotation * 180) / Math.PI;
        this.arrowElement.style.transform = `rotate(${degrees}deg)`;
        this.element.style.display = rotation === 0 ? 'none' : 'block';
    }
}

export function ImageTiffViewer() {
    const queryParams = queryString.parse(window.location.search);
    const viewerRef = useRef<HTMLDivElement>(null);
    const [fileIndexing, setFileIndexing] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string | (string | null)[] | null>('');
    const jwt = useSessionStore(state => state.jwtToken);

    const getFileNameFromJwt = useCallback(() => {
        const jwtPayload = jwt.split('.')[1];
        const decodedPayload = JSON.parse(atob(jwtPayload.replace(/-/g, '+').replace(/_/g, '/')));
        return decodedPayload['path'];
    }, [jwt]);

    useEffect(() => {
        if (queryParams.source === 'internal') {
            let fileName = null;
            if (!!queryParams.fileName) {
                fileName = queryParams.fileName as string;
            }
            else {
                fileName = getFileNameFromJwt();
            }

            if (!!fileName) {
                setFileName(fileName);
            }
        }
        else if (queryParams.source === 'file-indexing') {
            setFileIndexing(true);
        }
    }, [fileName, fileIndexing]);

    const { data } = useGetFileInfo(fileName);
    const { data: dataPath } = useGetFilePathAndInfo(queryParams.fileToken, fileIndexing);

    const [ showRotateInfo, setShowRotateInfo ] = useState(true);

    const { tenantConfig, jwtToken } = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
        jwtToken: state.jwtToken
    }));

    useEffect(() => {
        if (data || dataPath) {
            if (!viewerRef.current || !(fileName || fileIndexing)) {
                return;
            }

            let extent = [0, 0, 0, 0];
            if (data) {
                extent = [0, 0, data.width, data.height];
            }
            else if (dataPath) {
                extent = [0, 0, dataPath.Width, dataPath.Height];
            }

            const viewportWidth = viewerRef.current.clientWidth;
            const viewportHeight = viewerRef.current.clientHeight;
            const imageWidth = extent[2];
            const imageHeight = extent[3];
            const fitResolution = Math.max(imageWidth / viewportWidth, imageHeight / viewportHeight);

            const resolutions: number[] = [];
            let res = fitResolution;
            while (res >= 1) {
                resolutions.push(res);
                res = res / 2;
            }
            if (resolutions[resolutions.length - 1] > 1) {
                resolutions.push(1);
            }

            const TILE_SIZE = 128;

            const tileGrid = new TileGrid({
                extent,
                tileSize: TILE_SIZE,
                origin: [0, imageHeight],
                resolutions,
            });

            const pixelProjection = new Projection({
                code: 'pixel',
                units: 'pixels',
                extent: extent
            });

            const tileSource = new TileImage({
                projection: pixelProjection,
                tileGrid: tileGrid,
                tileUrlFunction: () => {
                    return 'about:blank';
                },
                tileLoadFunction: async (tile, src) => {
                    const imageTile = tile as ImageTile;
                    const [z, x, y] = imageTile.getTileCoord();

                    const tileExtent = tileGrid.getTileCoordExtent([z, x, y]);
                    const bbox = `${tileExtent[0]},${tileExtent[1]},${tileExtent[2]},${tileExtent[3]}`;

                    let urlTile = '';
                    if (data) {
                        urlTile = `${tenantConfig?.endpoints.render}/FilesViewer/GetTile?fileName=${fileName}&WIDTH=${TILE_SIZE}&HEIGHT=${TILE_SIZE}&BBOX=${bbox}`;
                    }
                    else if (dataPath) {
                        urlTile = `${tenantConfig?.endpoints.render}/FilesViewer/GetTileS3?filePath=${dataPath.FilePath}&WIDTH=${TILE_SIZE}&HEIGHT=${TILE_SIZE}&BBOX=${bbox}`;
                    }

                    try {
                        const response = await fetchWithRetry(
                            () => fetch(urlTile, { headers: { Authorization: `Bearer ${jwtToken}` } }),
                            3
                        );
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        (imageTile.getImage() as HTMLImageElement).src = url;
                    } catch {
                        imageTile.setState(TileState.ERROR);
                    }
                }
            });

            const view = new View({
                projection: pixelProjection,
                extent,
                resolutions: tileGrid.getResolutions(),
                center: [extent[2] / 2, extent[3] / 2],
                zoom: 0,
            });

            const rotateControl = new RotateResetControl(() => {
                view.animate({ rotation: 0, duration: 250 });
            });
            rotateControl.setRotation(0);

            const map = new Map({
                target: viewerRef.current,
                layers: [
                    new TileLayer({
                        source: tileSource,
                    }),
                ],
                controls: defaultControls({ rotate: false }).extend([rotateControl]),
                view,
            });

            view.on('change:rotation', () => {
                rotateControl.setRotation(view.getRotation());
            });

            view.fit(extent, { size: map.getSize() });
        }
    }, [data, dataPath]);

    return (
        <Fragment>
            <Box
                ref={viewerRef}
                sx={{
                    width: '100vw',
                    height: '100vh',
                    border: '1px solid #ccc',
                }}
            >
            </Box>
            <Stack position='absolute' right='30px' bottom='20px'>
                <Collapse in={showRotateInfo}>
                    <Alert
                        severity='info'
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setShowRotateInfo(false);
                                }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                    >
                    Use alt + shift + drag to rotate the screen
                    </Alert>
                </Collapse>
            </Stack>
        </Fragment>
    );
}
